import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { StickerPickerInline, StickerMessage } from "./StickerPicker";
import { useApp, api } from "./context";
import { Avatar, Btn, TierBadge } from "./components";

const PostMenu = ({ post, isMe, isAdmin, onClose, anchorRef }) => {
  const { c, flash, doDelete } = useApp();
  const ref = useRef(null);
  const [coords, setCoords] = useState(null);

  useEffect(() => {
    if (anchorRef?.current) {
      const r = anchorRef.current.getBoundingClientRect();
      setCoords({ top: r.bottom + 4, right: window.innerWidth - r.right });
    }
  }, [anchorRef]);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target) && !anchorRef?.current?.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", h);
    const closeOnScroll = () => onClose();
    window.addEventListener("scroll", closeOnScroll, true);
    return () => {
      document.removeEventListener("mousedown", h);
      window.removeEventListener("scroll", closeOnScroll, true);
    };
  }, [onClose, anchorRef]);

  const Item = ({ icon, label, onClick, danger }) => (
    <button onClick={(e) => { e.stopPropagation(); onClick(); onClose(); }} style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "10px 14px", border: "none", background: "transparent", cursor: "pointer", fontSize: 13, fontWeight: 600, color: danger ? c.redTxt : c.text, textAlign: "left" }}>
      <span style={{ fontSize: 15, width: 20, textAlign: "center" }}>{icon}</span>{label}
    </button>
  );
  const handleReport = async () => {
    const res = await api("/reports", { method: "POST", body: JSON.stringify({ post_id: post.id, reason: "Reported by user" }) });
    flash(res.ok ? "Post reported." : (res.message || "Could not report"), res.ok ? "success" : "error");
  };
  const handleMute = () => {
    const muted = JSON.parse(localStorage.getItem("gc_muted") || "[]");
    if (!muted.includes(String(post.authorId))) { muted.push(String(post.authorId)); localStorage.setItem("gc_muted", JSON.stringify(muted)); }
    flash("User muted.");
  };
  const handleBlock = async () => {
    const res = await api(`/profile/${post.authorId}/block`, { method: "POST" });
    flash(res.ok ? "User blocked" : (res.message || "Failed"), "error");
  };
  const handleAdminDelete = async () => {
    const res = await api(`/admin/posts/${post.id}/moderate`, { method: "POST", body: JSON.stringify({ action: "delete" }) });
    if (res.ok) { doDelete(post.id); flash("Post removed"); } else flash(res.message || "Failed", "error");
  };
  const handleAdminBan = async () => {
    const res = await api(`/admin/users/${post.authorId}/ban`, { method: "POST", body: JSON.stringify({ reason: "Banned from post action" }) });
    flash(res.ok ? "User banned" : (res.message || "Failed"), "error");
  };

  if (!coords) return null;

  return createPortal(
    <div ref={ref} style={{ position: "fixed", top: coords.top, right: coords.right, minWidth: 200, background: c.card, borderRadius: 14, border: `1px solid ${c.border}`, boxShadow: c.shd, zIndex: 9999, overflow: "hidden", padding: "4px 0" }}>
      {isMe && <Item icon="✏️" label="Edit post" onClick={() => flash("Edit coming soon!")} />}
      {isMe && <Item icon="🗑️" label="Delete post" onClick={() => doDelete(post.id)} danger />}
      {!isMe && <Item icon="🔇" label="Mute user" onClick={handleMute} />}
      {!isMe && <Item icon="🚩" label="Report post" onClick={handleReport} />}
      {!isMe && <Item icon="🚫" label="Block user" onClick={handleBlock} danger />}
      {isAdmin && !isMe && <div style={{ borderTop: `1px solid ${c.border}`, marginTop: 4, paddingTop: 4 }}>
        <div style={{ padding: "4px 14px", fontSize: 10, fontWeight: 700, color: c.faint }}>ADMIN</div>
        <Item icon="🗑️" label="Remove post" onClick={handleAdminDelete} danger />
        <Item icon="⛔" label="Ban user" onClick={handleAdminBan} danger />
      </div>}
    </div>,
    document.body
  );
};

// ── Repost Menu ──
const RepostMenu = ({ post, onClose, onQuote, onReposted }) => {
  const { c, dark, flash } = useApp();
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [onClose]);

  const handleRepost = async () => {
    const res = await api(`/posts/${post.id}/repost`, { method: "POST" });
    if (res.ok) { flash("Reposted! 🔁"); onReposted?.(res.data?.repost_count); }
    else flash(res.data?.message || res.message || "Repost failed", "error");
    onClose();
  };

  return (
    <div ref={ref} style={{ position: "fixed", inset: 0, zIndex: 2000, display: "flex", alignItems: "flex-end", justifyContent: "center" }} onClick={onClose}>
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }} />
      <div onClick={e => e.stopPropagation()} style={{ position: "relative", background: dark ? "#1a1b2e" : "#fff", borderRadius: "20px 20px 0 0", padding: "8px 0 40px", width: "100%", maxWidth: 480, zIndex: 1 }}>
        <div style={{ width: 40, height: 4, borderRadius: 2, background: "#ffffff20", margin: "8px auto 16px" }} />
        {[
          { icon: "🔄", label: "Repost", desc: "Share instantly to your followers", color: "#10b981", onClick: handleRepost },
          { icon: "✏️", label: "Quote Post", desc: "Add your reaction — text, image or emoji", color: "#7c6cff", onClick: () => { onClose(); onQuote(); } },
        ].map((item, i) => (
          <button key={i} onClick={item.onClick} style={{ display: "flex", alignItems: "center", gap: 14, width: "100%", padding: "14px 20px", border: "none", background: "transparent", cursor: "pointer", textAlign: "left" }}>
            <div style={{ width: 42, height: 42, borderRadius: 12, background: `${item.color}18`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>{item.icon}</div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: item.color }}>{item.label}</div>
              <div style={{ fontSize: 12, color: "#8890b0", marginTop: 2 }}>{item.desc}</div>
            </div>
          </button>
        ))}
        <button onClick={onClose} style={{ display: "flex", alignItems: "center", gap: 14, width: "100%", padding: "14px 20px", border: "none", background: "transparent", cursor: "pointer", borderTop: "0.5px solid #ffffff10", marginTop: 4 }}>
          <div style={{ width: 42, height: 42, borderRadius: 12, background: "#ffffff08", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>✕</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#8890b0" }}>Cancel</div>
        </button>
      </div>
    </div>
  );
};

// ── Share Sheet ──
const ShareSheet = ({ post, authorName, onClose }) => {
  const { c, dark, flash } = useApp();
  const url = `${window.location.origin}/post/${post.id}`;

  const handleCopyLink = () => { navigator.clipboard?.writeText(url); flash("Link copied! 🔗"); onClose(); };
  const handleWhatsApp = () => { window.open(`https://wa.me/?text=${encodeURIComponent(`Check this out on Geilstore Community:\n\n"${(post.content || "").slice(0, 100)}"\n\n${url}`)}`); onClose(); };
  const handleNativeShare = () => { if (navigator.share) navigator.share({ title: `Post by ${authorName}`, text: (post.content || "").slice(0, 100), url }); onClose(); };
  const handleScreenshot = () => { flash("Screenshot with watermark — coming soon! 📸"); onClose(); };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 2000, display: "flex", alignItems: "flex-end", justifyContent: "center" }} onClick={onClose}>
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }} />
      <div onClick={e => e.stopPropagation()} style={{ position: "relative", background: dark ? "#1a1b2e" : "#fff", borderRadius: "20px 20px 0 0", padding: "8px 0 40px", width: "100%", maxWidth: 480, zIndex: 1 }}>
        <div style={{ width: 40, height: 4, borderRadius: 2, background: "#ffffff20", margin: "8px auto 16px" }} />
        <div style={{ padding: "0 20px 14px", fontSize: 14, fontWeight: 700, color: dark ? "#e4e6f0" : "#1a1c2e" }}>Share Post</div>
        {[
          { icon: "🔗", label: "Copy Link", desc: "Copy post URL to clipboard", color: "#6c5ce7", onClick: handleCopyLink },
          { icon: "📲", label: "Share on WhatsApp", desc: "Send to your WhatsApp contacts", color: "#25D366", onClick: handleWhatsApp },
          { icon: "📸", label: "Screenshot + Watermark", desc: "Save image with Geilstore branding", color: "#f59e0b", onClick: handleScreenshot },
          { icon: "↗️", label: "Share to other apps", desc: "Use your device share menu", color: "#3b82f6", onClick: handleNativeShare },
        ].map((item, i) => (
          <button key={i} onClick={item.onClick} style={{ display: "flex", alignItems: "center", gap: 14, width: "100%", padding: "12px 20px", border: "none", background: "transparent", cursor: "pointer", textAlign: "left" }}>
            <div style={{ width: 40, height: 40, borderRadius: 12, background: `${item.color}18`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>{item.icon}</div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: dark ? "#e4e6f0" : "#1a1c2e" }}>{item.label}</div>
              <div style={{ fontSize: 11, color: "#8890b0", marginTop: 1 }}>{item.desc}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

// ── Quote Post Composer (full screen overlay) ──
const QuoteComposer = ({ post, authorName, authorPlan, authorGender, onClose }) => {
  const { c, dark, user, flash } = useApp();
  const [text, setText] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [posting, setPosting] = useState(false);
  const fileRef = useRef(null);
  const textRef = useRef(null);

  useEffect(() => { setTimeout(() => textRef.current?.focus(), 100); }, []);

  const handleImagePick = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { flash("Max 5MB", "error"); return; }
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = ev => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handlePost = async () => {
    if (!text.trim() && !imageFile) { flash("Add some text or image", "error"); return; }
    setPosting(true);
    const res = await api("/posts", {
      method: "POST",
      body: JSON.stringify({ content: text.trim(), quote_id: post.id, topic_id: 1 }),
    });
    setPosting(false);
    if (res.ok) { flash("Quote posted! ✨"); onClose(); }
    else flash(res.message || "Failed", "error");
  };

  const genderColor = { M: "#3b82f6", F: "#e91e8c", B: "#a855f7", NB: "#10b981" };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 3000, background: dark ? "#0b0c10" : "#f4f5f9", display: "flex", flexDirection: "column", fontFamily: "'DM Sans',sans-serif" }}>
      {/* Top bar */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", borderBottom: `0.5px solid ${dark ? "#ffffff12" : "#e0e2eb"}`, background: dark ? "#0b0c10" : "#fff" }}>
        <button onClick={onClose} style={{ background: "none", border: "none", color: dark ? "#8890b0" : "#6b7094", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
        <div style={{ fontSize: 14, fontWeight: 700, color: dark ? "#e4e6f0" : "#1a1c2e" }}>Quote Post</div>
        <button onClick={handlePost} disabled={posting || (!text.trim() && !imageFile)} style={{ padding: "7px 18px", borderRadius: 20, border: "none", background: "#7c6cff", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", opacity: (posting || (!text.trim() && !imageFile)) ? 0.5 : 1 }}>
          {posting ? "Posting..." : "Post ✨"}
        </button>
      </div>

      {/* Composer body */}
      <div style={{ flex: 1, overflowY: "auto", padding: "16px" }}>
        {/* My avatar + input */}
        <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
          <div style={{ width: 40, height: 40, borderRadius: 20, background: user?.avatarUrl ? `url(${user.avatarUrl}) center/cover` : "linear-gradient(135deg,#6c5ce7,#e91e8c)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, overflow: "hidden", flexShrink: 0 }}>
            {!user?.avatarUrl && (user?.emoji || "👤")}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: dark ? "#e4e6f0" : "#1a1c2e", marginBottom: 6 }}>{user?.name || "You"}</div>
            <textarea
              ref={textRef}
              value={text}
              onChange={e => setText(e.target.value.slice(0, 500))}
              placeholder="Add your reaction — text, emoji, or just a photo..."
              style={{ width: "100%", minHeight: 80, background: "transparent", border: "none", outline: "none", resize: "none", fontSize: 15, color: dark ? "#e4e6f0" : "#1a1c2e", lineHeight: 1.6, fontFamily: "inherit" }}
            />
            <div style={{ fontSize: 11, color: dark ? "#505878" : "#9ca0b8", textAlign: "right" }}>{text.length}/500</div>
          </div>
        </div>

        {/* Image preview */}
        {imagePreview && (
          <div style={{ position: "relative", marginBottom: 14 }}>
            <img src={imagePreview} alt="preview" style={{ width: "100%", maxHeight: 240, objectFit: "cover", borderRadius: 12, display: "block" }} />
            <button onClick={() => { setImageFile(null); setImagePreview(null); }} style={{ position: "absolute", top: 8, right: 8, width: 28, height: 28, borderRadius: 14, border: "none", background: "rgba(0,0,0,0.6)", color: "#fff", fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
          </div>
        )}

        {/* Quoted post */}
        <div style={{ background: dark ? "#12131a" : "#f8f8ff", borderRadius: 12, border: `0.5px solid ${dark ? "#252840" : "#e0e2eb"}`, padding: 12, cursor: "pointer" }} onClick={() => {}}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <div style={{ width: 24, height: 24, borderRadius: 12, background: "linear-gradient(135deg,#f59e0b,#ef4444)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "#fff", flexShrink: 0 }}>👤</div>
            <span style={{ fontSize: 12, fontWeight: 700, color: dark ? "#e4e6f0" : "#1a1c2e" }}>{authorName}</span>
            {authorGender && (
              <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 6, background: `${genderColor[authorGender] || "#6c5ce7"}20`, color: genderColor[authorGender] || "#6c5ce7" }}>{authorGender}</span>
            )}
            <span style={{ marginLeft: "auto", fontSize: 10, color: "#7c6cff" }}>↗ View original</span>
          </div>
          <div style={{ fontSize: 13, color: dark ? "#8890b0" : "#6b7094", lineHeight: 1.5 }}>{(post.content || "").slice(0, 120)}{(post.content || "").length > 120 ? "..." : ""}</div>
        </div>
      </div>

      {/* Bottom toolbar */}
      <div style={{ borderTop: `0.5px solid ${dark ? "#ffffff12" : "#e0e2eb"}`, padding: "10px 16px", background: dark ? "#0b0c10" : "#fff", display: "flex", gap: 8, alignItems: "center" }}>
        <button onClick={() => fileRef.current?.click()} style={{ width: 36, height: 36, borderRadius: 10, border: `1px solid ${dark ? "#252840" : "#e0e2eb"}`, background: "transparent", fontSize: 18, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>📷</button>
        <button onClick={() => setText(t => t + "🔥")} style={{ width: 36, height: 36, borderRadius: 10, border: `1px solid ${dark ? "#252840" : "#e0e2eb"}`, background: "transparent", fontSize: 18, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>😊</button>
        <button onClick={() => setText(t => t + "🔥")} style={{ padding: "6px 12px", borderRadius: 20, border: `1px solid ${dark ? "#252840" : "#e0e2eb"}`, background: "transparent", fontSize: 18, cursor: "pointer" }}>🔥</button>
        <button onClick={() => setText(t => t + "❤️")} style={{ padding: "6px 12px", borderRadius: 20, border: `1px solid ${dark ? "#252840" : "#e0e2eb"}`, background: "transparent", fontSize: 18, cursor: "pointer" }}>❤️</button>
        <button onClick={() => setText(t => t + "😂")} style={{ padding: "6px 12px", borderRadius: 20, border: `1px solid ${dark ? "#252840" : "#e0e2eb"}`, background: "transparent", fontSize: 18, cursor: "pointer" }}>😂</button>
        <button onClick={() => setText(t => t + "💯")} style={{ padding: "6px 12px", borderRadius: 20, border: `1px solid ${dark ? "#252840" : "#e0e2eb"}`, background: "transparent", fontSize: 18, cursor: "pointer" }}>💯</button>
        <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleImagePick} />
      </div>
    </div>
  );
};

// ── Comment Item (module-scope, stable identity across PostCard re-renders) ──
const CommentItem = ({
  cm, depth = 0, postId, user, c, dark,
  expandedReplies, setExpandedReplies, replyingTo, setReplyingTo,
  commentTexts, setCommentTexts, doComment, doLikeComment, doEditComment,
  editingComment, setEditingComment, editCommentText, setEditCommentText,
  setViewingUser, go, getAuthorName,
}) => {
  const cmName = String(cm.authorId) === String(user?.id) ? (user?.name || "You") : (cm.authorName || getAuthorName(cm.authorId));
  const isMine = String(cm.authorId) === String(user?.id);
  const repKey = `${postId}-${cm.id}`;
  const hasReplies = cm.replies && cm.replies.length > 0;
  const showReplies = expandedReplies[repKey];
  const isEditing = editingComment === cm.id;
  const canEdit = isMine && cm.createdAt && (Date.now() - new Date(cm.createdAt).getTime()) < 1800000;
  const doSaveEdit = async () => { const ok = await doEditComment(cm.id, editCommentText, postId); if (ok) setEditingComment(null); };
  const isReplying = replyingTo[postId] === cm.id;
  const replyKey = `${postId}-reply-${cm.id}`;

  return (
    <div style={{ marginBottom: depth === 0 ? 8 : 4 }}>
      <div style={{ display: "flex", gap: 7 }}>
        <Avatar id={cm.authorId} size={depth === 0 ? 28 : 22} pic={cm.authorPic}
          onClick={() => { if (!isMine) { setViewingUser(cm.authorId); go("user_profile"); } }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ background: c.hover || (dark ? "#ffffff08" : "#f8f8f8"), borderRadius: 12, padding: "8px 12px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 2 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: c.text }}>{cmName}</span>
              <TierBadge plan={cm.authorPlan || "free"} verified={cm.authorVerified} size={12} />
              {depth > 0 && <span style={{ fontSize: 10, color: c.faint }}>· reply</span>}
            </div>
            {isEditing ? (
              <div style={{ display: "flex", gap: 5, marginTop: 4 }}>
                <input value={editCommentText} onChange={e => setEditCommentText(e.target.value)}
                  style={{ flex: 1, padding: "6px 10px", borderRadius: 8, border: `1px solid ${c.pri}`, background: c.input, color: c.text, fontSize: 12, outline: "none", fontFamily: "inherit" }}
                  onKeyDown={e => e.key === "Enter" && doSaveEdit()} autoFocus />
                <button onClick={doSaveEdit} style={{ padding: "5px 10px", borderRadius: 8, border: "none", background: c.pri, color: "#fff", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>Save</button>
                <button onClick={() => setEditingComment(null)} style={{ padding: "5px 8px", borderRadius: 8, border: `1px solid ${c.border}`, background: "transparent", color: c.sub, fontSize: 10, cursor: "pointer" }}>✕</button>
              </div>
            ) : (
              <div style={{ fontSize: 13, color: c.text, lineHeight: 1.5 }}>
                {cm.sticker && <StickerMessage sticker={{ url: cm.sticker.url || cm.sticker_url, name: cm.sticker.name || "sticker" }} darkMode={!!dark} />}
                {cm.content}
              </div>
            )}
          </div>

          {/* Action row */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4, paddingLeft: 4 }}>
            <span style={{ fontSize: 10, color: c.faint }}>{cm.time}</span>
            <button onClick={() => doLikeComment(postId, cm.id)}
              style={{ border: "none", background: "none", padding: 0, fontSize: 11, fontWeight: 600, color: cm.liked ? "#e91e8c" : c.faint, cursor: "pointer", display: "flex", alignItems: "center", gap: 3 }}>
              {cm.liked ? "❤️" : "🤍"}{cm.likes > 0 && <span>{cm.likes}</span>}
            </button>
            <button
              onClick={() => setReplyingTo(prev => ({ ...prev, [postId]: prev[postId] === cm.id ? null : cm.id }))}
              style={{ border: "none", background: "none", padding: 0, fontSize: 11, fontWeight: 600, color: c.pri, cursor: "pointer" }}>
              Reply
            </button>
            {canEdit && !isEditing && (
              <button onClick={() => { setEditingComment(cm.id); setEditCommentText(cm.content); }}
                style={{ border: "none", background: "none", padding: 0, fontSize: 10, color: c.faint, cursor: "pointer" }}>
                Edit
              </button>
            )}
          </div>

          {/* Inline reply input — appears under ANY comment at ANY depth */}
          {isReplying && (
            <div style={{ marginTop: 6, padding: "8px 10px", background: c.priBg, borderRadius: 10, border: `1.5px solid ${c.pri}40` }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontSize: 10, color: c.pri, fontWeight: 700 }}>Replying to {cmName}</span>
                <button onClick={() => setReplyingTo(prev => ({ ...prev, [postId]: null }))}
                  style={{ border: "none", background: "none", color: c.faint, cursor: "pointer", fontSize: 10, fontWeight: 600 }}>✕</button>
              </div>
              <div style={{ display: "flex", gap: 5 }}>
                <input
                  autoFocus
                  style={{ flex: 1, padding: "7px 10px", borderRadius: 8, border: `1px solid ${c.pri}`, background: c.input, color: c.text, fontSize: 12, outline: "none", fontFamily: "inherit" }}
                  placeholder={`Reply to ${cmName}...`}
                  value={commentTexts[replyKey] || ""}
                  onChange={e => { const v = e.target.value; setCommentTexts(prev => ({ ...prev, [replyKey]: v })); }}
                  onKeyDown={e => { if (e.key === "Enter") doComment(postId); }}
                />
                <Btn primary small onClick={() => doComment(postId)}>↩</Btn>
              </div>
            </div>
          )}

          {/* Nested replies — recursive, unlimited depth */}
          {hasReplies && !showReplies && (
            <button onClick={() => setExpandedReplies(prev => ({ ...prev, [repKey]: true }))}
              style={{ border: "none", background: "none", padding: "4px 4px 0", fontSize: 11, fontWeight: 700, color: c.pri, cursor: "pointer" }}>
              ↳ View {cm.replies.length} {cm.replies.length === 1 ? "reply" : "replies"}
            </button>
          )}
          {hasReplies && showReplies && (
            <div style={{ marginTop: 6, borderLeft: `2px solid ${c.border}`, paddingLeft: 8 }}>
              <button onClick={() => setExpandedReplies(prev => ({ ...prev, [repKey]: false }))}
                style={{ border: "none", background: "none", padding: "0 0 4px", fontSize: 10, fontWeight: 700, color: c.faint, cursor: "pointer" }}>
                ↳ Hide replies
              </button>
              {cm.replies.map(r => (
                <CommentItem
                  key={r.id} cm={r} depth={depth + 1} postId={postId} user={user} c={c} dark={dark}
                  expandedReplies={expandedReplies} setExpandedReplies={setExpandedReplies}
                  replyingTo={replyingTo} setReplyingTo={setReplyingTo}
                  commentTexts={commentTexts} setCommentTexts={setCommentTexts}
                  doComment={doComment} doLikeComment={doLikeComment} doEditComment={doEditComment}
                  editingComment={editingComment} setEditingComment={setEditingComment}
                  editCommentText={editCommentText} setEditCommentText={setEditCommentText}
                  setViewingUser={setViewingUser} go={go} getAuthorName={getAuthorName}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Main PostCard ──
const PostCard = ({ post, full }) => {
  const [commentSticker, setCommentSticker] = useState({});
  const [showCommentStickerPicker, setShowCommentStickerPicker] = useState({});
  const [showMenu, setShowMenu] = useState(false);
  const [editingComment, setEditingComment] = useState(null);
  const [editCommentText, setEditCommentText] = useState("");
  const [truncated, setTruncated] = useState(true);
  const [showTipModal, setShowTipModal] = useState(false);
  const [tipAmount, setTipAmount] = useState(10);
  const [tipping, setTipping] = useState(false);
  const [showRepostMenu, setShowRepostMenu] = useState(false);
  const [showShareSheet, setShowShareSheet] = useState(false);
  const [showQuoteComposer, setShowQuoteComposer] = useState(false);
  const [isFollowing, setIsFollowing] = useState(post.isFollowing || false);
  const [followLoading, setFollowLoading] = useState(false);
  const [blurRevealed, setBlurRevealed] = useState(false);
  const [repostCount, setRepostCount] = useState(post.reposts || 0);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const menuBtnRef = useRef(null);

  const { c, dark, go, user, flash, expandedComments, setExpandedComments, commentTexts, setCommentTexts,
    doLike, doBookmark, doDelete, doComment, doEditComment, doLikeComment, doVotePoll, loadPostDetail,
    replyingTo, setReplyingTo, expandedReplies, setExpandedReplies,
    setSelectedPost, setViewingUser, getAuthorName, getAuthorVerified, getAuthorTrust, getAuthorPlan } = useApp();

  const isMe = String(post.authorId) === String(user?.id) || post.authorId === "me";
  const isAdmin = user?.isAdmin || false;
  const authorName = isMe ? (user?.name || "You") : (post.authorName || getAuthorName(post.authorId));
  const authorVerified = isMe ? user?.verified : (post.authorVerified || getAuthorVerified(post.authorId));
  const authorPlan = isMe ? (user?.plan || "free") : (post.authorPlan || getAuthorPlan?.(post.authorId) || "free");
  const authorTrust = isMe ? (user?.trustScore || 0) : (post.authorTrust || getAuthorTrust(post.authorId));
  const authorGender = post.authorGender || null; // M, F, B, NB
  const isSpicy = post.topic === "spicy";
  const exp = full || expandedComments[post.id];
  const CONTENT_LIMIT = 200;
  const isLong = (post.content || "").length > CONTENT_LIMIT;

  const genderColor = { M: "#3b82f6", F: "#e91e8c", B: "#a855f7", NB: "#10b981" };

  const cardBorder = authorPlan === "elite" ? "1.5px solid #f59e0b40" : authorPlan === "premium" ? "1.5px solid #7c6cff30" : `1px solid ${c.border}`;
  const cardShadow = authorPlan === "elite" ? "0 2px 12px rgba(245,158,11,0.08)" : authorPlan === "premium" ? "0 2px 8px rgba(124,108,255,0.06)" : "none";

  const toggleComments = () => {
    if (!full) { setSelectedPost(post); go("post_detail"); return; }
    const next = !expandedComments[post.id];
    setExpandedComments({ ...expandedComments, [post.id]: next });
    if (next && (!post.commentList || post.commentList.length === 0) && post.comments > 0) loadPostDetail(post.id);
  };

  const handleFollow = async () => {
    if (isMe || followLoading) return;
    setFollowLoading(true);
    const newState = !isFollowing;
    setIsFollowing(newState);
    const res = await api(`/profile/${post.authorId}/follow`, { method: "POST" });
    setFollowLoading(false);
    if (res.ok) {
      flash(newState ? `Following ${authorName}! ✅` : `Unfollowed ${authorName}`);
    } else {
      setIsFollowing(!newState); // revert
      flash(res.message || "Failed", "error");
    }
  };

  const handleTip = async () => {
    setTipping(true);
    const res = await api("/wallet/send", { method: "POST", body: JSON.stringify({ user_id: post.authorId, amount: tipAmount, message: `Tip for your post 🪙` }) });
    setTipping(false);
    if (res.ok) { flash(`Tipped ${tipAmount} Gtks to ${authorName}! 🪙`); setShowTipModal(false); }
    else flash(res.message || "Tip failed", "error");
  };

  return (
    <>
      {/* Repost menu overlay */}
      {showRepostMenu && (
        <RepostMenu
          post={post}
          onClose={() => setShowRepostMenu(false)}
          onQuote={() => setShowQuoteComposer(true)}
          onReposted={(count) => setRepostCount(typeof count === "number" ? count : repostCount + 1)}
        />
      )}

      {/* Share sheet overlay */}
      {showShareSheet && (
        <ShareSheet
          post={post}
          authorName={authorName}
          onClose={() => setShowShareSheet(false)}
        />
      )}

      {/* Quote composer overlay */}
      {showQuoteComposer && (
        <QuoteComposer
          post={post}
          authorName={authorName}
          authorPlan={authorPlan}
          authorGender={authorGender}
          onClose={() => setShowQuoteComposer(false)}
        />
      )}

      {lightboxUrl && (
        <div onClick={() => setLightboxUrl(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.95)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <button onClick={() => setLightboxUrl(null)} style={{ position: "absolute", top: 16, right: 16, width: 36, height: 36, borderRadius: 18, border: "none", background: "rgba(255,255,255,0.15)", color: "#fff", fontSize: 20, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
          <img src={lightboxUrl} alt="" style={{ maxWidth: "100%", maxHeight: "90vh", objectFit: "contain", borderRadius: 12 }} />
        </div>
      )}

      <div style={{ background: c.card, borderRadius: 16, border: cardBorder, boxShadow: cardShadow, padding: "16px 16px 12px", marginBottom: 10, position: "relative", overflow: "hidden" }}>

        {/* Repost header — if this is a repost */}
        {post.repostedBy && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10, fontSize: 11, color: c.faint }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={c.faint} strokeWidth="2"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>
            <span>{post.repostedBy} reposted</span>
          </div>
        )}

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <div style={{ position: "relative", flexShrink: 0 }}>
            <Avatar id={post.authorId} size={42} pic={post.authorPic} onClick={() => { if (!isMe) { setViewingUser(post.authorId); go("user_profile"); } else go("profile"); }} />
            <div style={{ position: "absolute", bottom: 1, right: 1, width: 10, height: 10, borderRadius: 5, background: "#10b981", border: `2px solid ${dark ? c.card : "#fff"}` }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: c.text, cursor: "pointer" }} onClick={() => { if (!isMe) { setViewingUser(post.authorId); go("user_profile"); } }}>{authorName}</span>
              <TierBadge plan={authorPlan} verified={authorVerified} size={14} />
              {/* Gender pill — only if user set it to public */}
              {authorGender && (
                <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 6, background: `${genderColor[authorGender] || "#6c5ce7"}20`, color: genderColor[authorGender] || "#6c5ce7" }}>{authorGender}</span>
              )}
              {/* Follow button — hide if own post */}
              {!isMe && !full && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleFollow(); }}
                  disabled={followLoading}
                  style={{ padding: "2px 8px", borderRadius: 6, border: `1px solid ${isFollowing ? "#10b98160" : c.pri}`, background: isFollowing ? "#10b98115" : "transparent", color: isFollowing ? "#10b981" : c.pri, fontSize: 10, fontWeight: 700, cursor: "pointer", opacity: followLoading ? 0.6 : 1 }}
                >
                  {isFollowing ? "Following ✓" : "+ Follow"}
                </button>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 1 }}>
              <span style={{ fontSize: 11, color: c.faint }}>{post.time}</span>
              {post.authorLocation && <>
                <span style={{ fontSize: 10, color: c.faint }}>·</span>
                <span style={{ fontSize: 11, color: c.faint, display: "flex", alignItems: "center", gap: 2 }}>
                  <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#e91e8c" strokeWidth="3"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>
                  {post.authorLocation}
                </span>
              </>}
            </div>
          </div>
          <div style={{ position: "relative" }}>
            <button ref={menuBtnRef} onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }} style={{ width: 32, height: 32, borderRadius: 8, border: "none", background: showMenu ? (dark ? "#ffffff10" : "#f0f0f0") : "transparent", color: c.faint, fontSize: 18, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>⋮</button>
            {showMenu && <PostMenu post={post} isMe={isMe} isAdmin={isAdmin} onClose={() => setShowMenu(false)} anchorRef={menuBtnRef} />}
          </div>
        </div>

        {/* Content — with blur for sensitive topics */}
        {isSpicy && !blurRevealed && !isMe ? (
          <div style={{ position: "relative", marginBottom: 10 }}>
            <div style={{ filter: "blur(6px)", fontSize: 14, lineHeight: 1.7, color: c.text, userSelect: "none", pointerEvents: "none" }}>
              {(post.content || "").slice(0, 100)}...
            </div>
            <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6 }}>
              <span style={{ fontSize: 11, color: "#ef4444", fontWeight: 700, background: "#ef444420", padding: "2px 10px", borderRadius: 20 }}>🔞 Sensitive Content</span>
              <button onClick={() => setBlurRevealed(true)} style={{ padding: "6px 16px", borderRadius: 20, border: "none", background: "rgba(0,0,0,0.6)", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>👁 Tap to view</button>
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 14, lineHeight: 1.7, color: c.text, marginBottom: 10, wordBreak: "break-word", whiteSpace: "pre-wrap", cursor: "pointer" }} onClick={() => { setSelectedPost(post); go("post_detail"); }}>
            {isLong && truncated ? (post.content || "").slice(0, CONTENT_LIMIT) + "..." : (post.content || "")}
            {isLong && <span onClick={e => { e.stopPropagation(); setTruncated(!truncated); }} style={{ color: c.pri, fontSize: 13, fontWeight: 600, marginLeft: 4, cursor: "pointer" }}>{truncated ? "read more" : " show less"}</span>}
          </div>
        )}
{/* Media */}
        {(post.media_type === "video" || post.mediaType === "video") && (post.media_url || post.mediaUrl) && (
          <div style={{ marginBottom: 10 }}>
            <video src={post.media_url || post.mediaUrl} controls style={{ width: "100%", maxHeight: 320, borderRadius: 12, display: "block", background: "#000" }} />
          </div>
        )}
        {(post.media_type === "image" || post.mediaType === "image") && (post.media_url || post.mediaUrl || post.media_urls || post.mediaUrls) && (() => {
  // Safely parse media_urls — may be string, array, or null
  let urls = post.media_urls || post.mediaUrls;
  if (typeof urls === "string") { try { urls = JSON.parse(urls); } catch { urls = null; } }
  if (!Array.isArray(urls) || urls.length === 0) {
    const single = post.media_url || post.mediaUrl;
    urls = single ? [single] : [];
  }
  if (urls.length === 0) return null;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "grid", gridTemplateColumns: urls.length === 1 ? "1fr" : "1fr 1fr", gap: 6 }}>
        {urls.map((url, i) => (
          <img key={i} src={url} alt="" style={{ width: "100%", height: urls.length === 1 ? "auto" : 160, maxHeight: urls.length === 1 ? 500 : 200, objectFit: "cover", borderRadius: 10, display: "block", cursor: "pointer" }} onClick={() => setLightboxUrl(url)} />
        ))}
      </div>
    </div>
  );
})()}

        {/* Sticker */}
        {post.sticker && (
          <div style={{ marginBottom: 10 }}>
            <img src={post.sticker.url} alt={post.sticker.name || "sticker"} style={{ width: "100%", maxWidth: 280, maxHeight: 280, objectFit: "contain", borderRadius: 12, display: "block" }} />
          </div>
        )}

        {/* Reposted post — if this is a plain repost */}
        {post.repostedPost && (
          <div
            onClick={(e) => { e.stopPropagation(); }}
            style={{ background: dark ? "#12131a" : "#f8f8ff", borderRadius: 12, border: `0.5px solid ${dark ? "#252840" : "#e0e2eb"}`, padding: 12, marginBottom: 10 }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <div style={{ width: 22, height: 22, borderRadius: 11, background: "linear-gradient(135deg,#6c5ce7,#e91e8c)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#fff", flexShrink: 0 }}>👤</div>
              <span style={{ fontSize: 12, fontWeight: 700, color: dark ? "#e4e6f0" : "#1a1c2e" }}>{post.repostedPost.authorName}</span>
              <span style={{ fontSize: 11, color: c.faint }}>{post.repostedPost.time}</span>
            </div>
            <div style={{ fontSize: 13, color: dark ? "#c8cce0" : "#3a3d55", lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {post.repostedPost.content || "📷 Media post"}
            </div>
          </div>
        )}

        {/* Quoted post — if this is a quote post */}
        {post.quotedPost && (
          <div
            onClick={(e) => { e.stopPropagation(); setSelectedPost(post.quotedPost); go("post_detail"); }}
            style={{ background: dark ? "#12131a" : "#f8f8ff", borderRadius: 12, border: `0.5px solid ${dark ? "#252840" : "#e0e2eb"}`, padding: 12, marginBottom: 10, cursor: "pointer" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <div style={{ width: 22, height: 22, borderRadius: 11, background: "linear-gradient(135deg,#f59e0b,#ef4444)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#fff", flexShrink: 0 }}>👤</div>
              <span style={{ fontSize: 12, fontWeight: 700, color: dark ? "#e4e6f0" : "#1a1c2e" }}>{post.quotedPost.authorName}</span>
              {post.quotedPost.authorGender && (
                <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 5px", borderRadius: 5, background: `${genderColor[post.quotedPost.authorGender] || "#6c5ce7"}20`, color: genderColor[post.quotedPost.authorGender] || "#6c5ce7" }}>{post.quotedPost.authorGender}</span>
              )}
              <span style={{ fontSize: 11, color: c.faint }}>{post.quotedPost.time}</span>
              <span style={{ marginLeft: "auto", fontSize: 10, color: "#7c6cff", fontWeight: 600 }}>↗ View</span>
            </div>
            <div style={{ fontSize: 13, color: dark ? "#8890b0" : "#6b7094", lineHeight: 1.5 }}>
              {(post.quotedPost.content || "").slice(0, 120)}{(post.quotedPost.content || "").length > 120 ? "..." : ""}
            </div>
          </div>
        )}

        {/* Poll — Fixed: no green/red, just purple for leading */}
        {post.poll && (() => {
          const hasVoted = !!post.poll.voted;
          const maxVotes = Math.max(...post.poll.options.map(o => o.votes), 1);
          return (
            <div style={{ borderRadius: 14, border: `1px solid ${c.border}`, padding: 14, marginBottom: 10, background: dark ? "#ffffff05" : "#f8f7ff" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: c.text, marginBottom: 10 }}>{post.poll.question}</div>
              {post.poll.options.map((opt) => {
                const pct = post.poll.totalVotes > 0 ? Math.round(opt.votes / post.poll.totalVotes * 100) : 0;
                const isVoted = post.poll.voted === opt.id;
                const isLeading = opt.votes === maxVotes && opt.votes > 0;
                const barColor = isVoted ? "#7c6cff" : isLeading ? "#7c6cff" : (dark ? "#ffffff15" : "#e0e2eb");
                return (
                  <button key={opt.id} onClick={() => !hasVoted && doVotePoll(post.id, opt.id)} disabled={hasVoted} style={{ width: "100%", padding: 0, borderRadius: 10, border: `1.5px solid ${isVoted ? "#7c6cff60" : c.border}`, background: "transparent", marginBottom: 6, cursor: hasVoted ? "default" : "pointer", overflow: "hidden", textAlign: "left", display: "block" }}>
                    <div style={{ position: "relative", padding: "10px 12px" }}>
                      {hasVoted && <div style={{ position: "absolute", top: 0, left: 0, bottom: 0, width: `${pct}%`, background: isVoted ? "#7c6cff20" : isLeading ? "#7c6cff10" : "#ffffff08", transition: "width 0.6s ease-out", borderRadius: 8 }} />}
                      <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 8 }}>
                        {hasVoted && isVoted && <div style={{ width: 16, height: 16, borderRadius: 8, background: "#7c6cff", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}><svg width="9" height="9" viewBox="0 0 24 24"><path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z" fill="white"/></svg></div>}
                        <span style={{ fontSize: 13, fontWeight: isVoted ? 700 : 400, color: isVoted ? "#7c6cff" : c.text, flex: 1 }}>{opt.text}</span>
                        {hasVoted && <span style={{ fontSize: 12, fontWeight: 700, color: isVoted ? "#7c6cff" : c.faint }}>{pct}%</span>}
                      </div>
                    </div>
                  </button>
                );
              })}
              <div style={{ fontSize: 11, color: c.faint, marginTop: 4 }}>{post.poll.totalVotes} votes · Ends in {post.poll.endsIn}</div>
            </div>
          );
        })()}

        {/* Action Bar */}
        <div style={{ display: "flex", alignItems: "center", paddingTop: 10, borderTop: `1px solid ${dark ? "#ffffff08" : "#f0f0f0"}`, gap: 0 }}>
          {/* Like */}
          <button onClick={() => doLike(post.id)} style={{ display: "flex", alignItems: "center", gap: 4, padding: "6px 8px", borderRadius: 8, border: "none", background: "transparent", color: post.liked ? "#e91e8c" : c.faint, fontSize: 12, fontWeight: 600, cursor: "pointer", flex: 1, justifyContent: "center" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill={post.liked ? "#e91e8c" : "none"} stroke={post.liked ? "#e91e8c" : c.faint} strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
            <span>{post.likes || 0}</span>
          </button>
          {/* Comment */}
          <button onClick={toggleComments} style={{ display: "flex", alignItems: "center", gap: 4, padding: "6px 8px", borderRadius: 8, border: "none", background: "transparent", color: c.faint, fontSize: 12, fontWeight: 600, cursor: "pointer", flex: 1, justifyContent: "center" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c.faint} strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
            <span>{post.comments || 0}</span>
          </button>
          {/* Views */}
          <button style={{ display: "flex", alignItems: "center", gap: 4, padding: "6px 8px", borderRadius: 8, border: "none", background: "transparent", color: c.faint, fontSize: 12, fontWeight: 600, cursor: "default", flex: 1, justifyContent: "center" }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={c.faint} strokeWidth="2"><rect x="2" y="3" width="4" height="18" rx="1"/><rect x="9" y="8" width="4" height="13" rx="1"/><rect x="16" y="5" width="4" height="16" rx="1"/></svg>
            <span>{post.views || 0}</span>
          </button>
          {/* Repost — opens menu */}
          <button onClick={(e) => { e.stopPropagation(); setShowRepostMenu(true); }} style={{ display: "flex", alignItems: "center", gap: 4, padding: "6px 8px", borderRadius: 8, border: "none", background: "transparent", color: repostCount > 0 ? "#10b981" : c.faint, fontSize: 12, fontWeight: 600, cursor: "pointer", flex: 1, justifyContent: "center" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={repostCount > 0 ? "#10b981" : c.faint} strokeWidth="2"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>
            {repostCount > 0 && <span>{repostCount}</span>}
          </button>
          {/* Bookmark */}
          <button onClick={() => doBookmark(post.id)} style={{ display: "flex", alignItems: "center", gap: 4, padding: "6px 8px", borderRadius: 8, border: "none", background: "transparent", color: post.bookmarked ? "#f59e0b" : c.faint, fontSize: 12, fontWeight: 600, cursor: "pointer", flex: 1, justifyContent: "center" }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill={post.bookmarked ? "#f59e0b" : "none"} stroke={post.bookmarked ? "#f59e0b" : c.faint} strokeWidth="2"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>
          </button>
          {/* Tip */}
          <button onClick={() => !isMe && setShowTipModal(true)} style={{ display: "flex", alignItems: "center", gap: 4, padding: "6px 8px", borderRadius: 8, border: "none", background: "transparent", color: "#a855f7", fontSize: 12, fontWeight: 600, cursor: isMe ? "default" : "pointer", flex: 1, justifyContent: "center", opacity: isMe ? 0.4 : 1 }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#a855f7" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2" stroke="#a855f7"/><circle cx="12" cy="12" r="3" fill="#a855f7" fillOpacity="0.2"/></svg>
          </button>
          {/* Share — opens sheet */}
          <button onClick={(e) => { e.stopPropagation(); setShowShareSheet(true); }} style={{ display: "flex", alignItems: "center", gap: 4, padding: "6px 8px", borderRadius: 8, border: "none", background: "transparent", color: c.faint, fontSize: 12, fontWeight: 600, cursor: "pointer", flex: 1, justifyContent: "center" }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={c.faint} strokeWidth="2"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
          </button>
        </div>

        {/* Tip Modal */}
        {showTipModal && (
          <div onClick={() => setShowTipModal(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(6px)", zIndex: 1000, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
            <div onClick={e => e.stopPropagation()} style={{ background: dark ? "#1a1b2e" : "#fff", borderRadius: "20px 20px 0 0", padding: "24px 20px 40px", width: "100%", maxWidth: 480 }}>
              <div style={{ width: 40, height: 4, borderRadius: 2, background: c.border, margin: "0 auto 20px" }} />
              <div style={{ textAlign: "center", marginBottom: 20 }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>🪙</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: c.text, marginBottom: 4 }}>Tip {authorName}</div>
                <div style={{ fontSize: 12, color: c.faint }}>Send Gtks as appreciation</div>
              </div>
              <div style={{ display: "flex", gap: 8, justifyContent: "center", marginBottom: 16 }}>
                {[10, 20, 50, 100, 200].map(amt => (
                  <button key={amt} onClick={() => setTipAmount(amt)} style={{ padding: "8px 14px", borderRadius: 10, border: `2px solid ${tipAmount === amt ? "#a855f7" : c.border}`, background: tipAmount === amt ? "#a855f720" : "transparent", color: tipAmount === amt ? "#a855f7" : c.text, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>{amt}</button>
                ))}
              </div>
              <div style={{ textAlign: "center", fontSize: 12, color: c.faint, marginBottom: 16 }}>= KES {(tipAmount * 0.1).toFixed(0)}</div>
              <button onClick={handleTip} disabled={tipping} style={{ width: "100%", padding: "13px", borderRadius: 12, border: "none", background: "linear-gradient(135deg, #a855f7, #7c3aed)", color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer", opacity: tipping ? 0.7 : 1 }}>{tipping ? "Sending..." : `Send ${tipAmount} Gtks 🪙`}</button>
            </div>
          </div>
        )}

{/* Comments Section — only in post detail */}
        {full && exp && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${dark ? "#ffffff08" : "#f0f0f0"}` }}>
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              <input style={{ flex: 1, padding: "8px 13px", borderRadius: 10, border: `1px solid ${c.border}`, background: c.input, color: c.text, fontSize: 13, outline: "none", fontFamily: "inherit" }} placeholder={replyingTo[post.id] ? "Replying..." : "Write a comment..."} value={commentTexts[post.id] || ""} onChange={e => { const v = e.target.value; setCommentTexts(prev => ({ ...prev, [post.id]: v })); }} onKeyDown={e => { if(e.key === "Enter" && !replyingTo[post.id]) { doComment(post.id, commentSticker[post.id]?.id || null); setCommentSticker(p=>({...p,[post.id]:null})); }}} disabled={!!replyingTo[post.id]} />
              <button onClick={e => { e.stopPropagation(); setShowCommentStickerPicker(p => ({...p, [post.id]: !p[post.id]})); }} style={{ width:34, height:34, borderRadius:17, border:`1px solid ${c.border}`, background: showCommentStickerPicker[post.id] ? c.priBg : c.card, fontSize:16, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>🎭</button>
              {!replyingTo[post.id] && <Btn primary small onClick={() => { doComment(post.id, commentSticker[post.id]?.id || null); setCommentSticker(p=>({...p,[post.id]:null})); }}>Send</Btn>}
            </div>
            {showCommentStickerPicker[post.id] && (
              <div style={{ marginBottom: 8 }}>
                <StickerPickerInline darkMode={!!dark} onClose={() => setShowCommentStickerPicker(p=>({...p,[post.id]:false}))} onSelect={s => { setCommentSticker(p=>({...p,[post.id]:s})); setShowCommentStickerPicker(p=>({...p,[post.id]:false})); }} />
              </div>
            )}
            {commentSticker[post.id] && (
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8, padding:"6px 10px", background:c.priBg, borderRadius:10, border:`1px solid ${c.pri}40` }}>
                <StickerMessage sticker={commentSticker[post.id]} darkMode={!!dark} />
                <button onClick={() => setCommentSticker(p=>({...p,[post.id]:null}))} style={{ border:"none", background:"none", cursor:"pointer", color:c.sub, fontSize:18, padding:4 }}>✕</button>
              </div>
            )}
            {[...(post.commentList || [])].reverse().map(cm => (
  <CommentItem
    key={cm.id} cm={cm} depth={0} postId={post.id} user={user} c={c} dark={dark}
    expandedReplies={expandedReplies} setExpandedReplies={setExpandedReplies}
    replyingTo={replyingTo} setReplyingTo={setReplyingTo}
    commentTexts={commentTexts} setCommentTexts={setCommentTexts}
    doComment={doComment} doLikeComment={doLikeComment} doEditComment={doEditComment}
    editingComment={editingComment} setEditingComment={setEditingComment}
    editCommentText={editCommentText} setEditCommentText={setEditCommentText}
    setViewingUser={setViewingUser} go={go} getAuthorName={getAuthorName}
  />
))}
            {(post.commentList || []).length === 0 && !replyingTo[post.id] && <div style={{ textAlign: "center", padding: "12px 0", fontSize: 12, color: c.faint }}>No comments yet. Be the first!</div>}
          </div>
        )}
      </div>
    </>
  );
};

export default PostCard;